import { createMemo, mergeProps, type ParentComponent } from "solid-js";
import { Checkbox as ArkCheckbox } from "@ark-ui/solid";
import { isBoolean } from "lodash-es";
import Pill from "../pill/Pill";
import { getIconCategoryColor } from "../../utils/iconColorUtil";
import type { IconName } from "../icon/Icon";
import type { Category } from "../../types";
import Text from "../text/Text";
import { DynamicValueIconTile } from "../icon/IconTile";

type CheckboxFieldListItemProps = {
  title: string;
  dataName: string;
  icon: IconName;
  category: Category;
  checked: boolean;
  disabled?: boolean;
  setChecked: (value: boolean) => void;
};

const CheckboxFieldListItem: ParentComponent<CheckboxFieldListItemProps> = (rawProps) => {
  const props = mergeProps({ disabled: false }, rawProps);
  const checkboxColor = createMemo(
    () => (props.disabled ? "#9F9FA9" /* surface-active-disabled */ : "#3F5D8B") /* surface-active-brand-harsh */,
  );
  return (
    <ArkCheckbox.Root
      checked={props.checked}
      disabled={props.disabled}
      onCheckedChange={(e) => {
        if (isBoolean(e.checked)) {
          props.setChecked(e.checked);
        }
      }}
      class="flex justify-between gap-2 rounded-sm px-3 py-2.5"
      classList={{
        "bg-surface-active-brand": props.checked,
        "bg-surface-disabled": !props.checked && props.disabled,
        "cursor-pointer text-primary hover:bg-surface-hover": !props.disabled,
        "text-disabled": props.disabled,
      }}
    >
      <div class="group flex w-full min-w-0 shrink items-center gap-2.5">
        <ArkCheckbox.HiddenInput class="peer" />
        <ArkCheckbox.Control class="relative my-0.5 size-4 rounded-sm peer-focus-visible:outline-default">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <rect
              x="0.75"
              y="0.75"
              width="14.5"
              height="14.5"
              rx="3.25"
              fill="white"
              stroke="#D1D5DB"
              stroke-width="1.5"
            />
          </svg>
          <ArkCheckbox.Indicator class="absolute top-0 left-0">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect
                x="0.75"
                y="0.75"
                width="14.5"
                height="14.5"
                rx="3.25"
                fill={checkboxColor()}
                stroke={checkboxColor()}
                stroke-width="1.5"
              />
              <path
                d="M11.75 6L7.10938 10.6406L5 8.53125"
                stroke="white"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </ArkCheckbox.Indicator>
        </ArkCheckbox.Control>
        <ArkCheckbox.Label class="flex w-full shrink gap-1.5 overflow-x-auto text-base-regular">
          <DynamicValueIconTile
            icon={props.icon}
            iconClassList={{ [getIconCategoryColor(props.category)]: true }}
            size="sm"
          />
          <Text text={props.title} truncate />
          <span class="ml-auto">
            <Pill variant="neutral" label={props.dataName} />
          </span>
        </ArkCheckbox.Label>
      </div>
    </ArkCheckbox.Root>
  );
};

export default CheckboxFieldListItem;
